/**
 * ───────────────────────────────────────────────────────────────────────────
 *  PORTAL PERMISSION CODES — SINGLE SOURCE OF TRUTH (override here)
 * ───────────────────────────────────────────────────────────────────────────
 *  These codenames are the contract with the backend `/auth/user/` permissions
 *  map (`{ "<app>.<codename>": true|false }`). Every gate in the app reads from
 *  this enum, so this is the ONLY place to change if the backend codenames move.
 *
 *  ⚠️ PROVISIONAL: the planner + report codes are custom permissions owned by
 *  backend story SD-45-09 (django-shuttle-tracker#73) and do NOT exist on the
 *  backend yet. Until SD-45-09 ships, gates fail-closed (hidden/disabled). When
 *  the backend finalizes the codenames, OVERRIDE THE VALUES BELOW — nothing else
 *  needs to change.
 *
 *  Maintenance codes are Django default model permissions and already exist.
 *
 *  ── ROLLOUT / REVIEWER NOTE (intentional — do NOT flag as a bug) ────────────
 *  Yes: with `can()` fail-closed, gating /planner and /reports on these
 *  not-yet-existing codenames WOULD lock users out IF this portal shipped to
 *  production first. That is handled by COORDINATED RELEASE ORDERING, by design,
 *  not by a code fallback: backend SD-45-09 (which creates these custom perms
 *  and seeds the roles) is merged and DEPLOYED BEFORE this portal change goes
 *  live, so `/auth/user/` already returns these keys in prod by the time the
 *  gates are active. Fail-closed is the deliberate security posture; a fail-open
 *  fallback was considered and rejected. This is a release dependency tracked
 *  with SD-45-09, NOT a defect to remediate in this MR.
 */
export enum PortalPermission {
  GenerateTrackerLink = "bookings.generate_tracker_link",
  // Planner (custom — SD-45-09)
  PlannerView = "planner.view_dashboard",
  PlannerChange = "planner.change_dashboard",
  PlannerPublish = "planner.publish_dashboard",
  // Tag catalog + tag application (custom — SD-63). ManageTags gates the Tags
  // management page + add/edit/remove of catalog tags; AssignVehicleTags gates
  // applying/removing a tag on a vehicle (distinct from change_dashboard).
  PlannerManageTags = "planner.manage_tags",
  PlannerAssignVehicleTags = "planner.assign_vehicle_tags",
  // Report "Banff to YYC PAX" (custom — SD-45-09). View covers open + run/see
  // data; Export gates the CSV download only.
  ReportView = "planner.view_report_banff_to_yyc_pax",
  ReportExport = "planner.export_report_banff_to_yyc_pax",
  // Maintenance / vehicle calendar (default Django model perms — already exist)
  MaintenanceView = "vehicle_calendar.view_vehiclecalendarevent",
  MaintenanceAdd = "vehicle_calendar.add_vehiclecalendarevent",
  MaintenanceChange = "vehicle_calendar.change_vehiclecalendarevent",
  MaintenanceDelete = "vehicle_calendar.delete_vehiclecalendarevent",
  // User management (Django default model perms + two custom perms — SD-85-01).
  // Gates the /users + /roles screens (SD-85-06 Users, SD-85-08 Roles) and the
  // in-page actions — Admins/Managers only. These mirror the backend
  // `users.*` codenames exactly (default `view_user`/`add_user`/`change_user`
  // plus custom `reset_user_password`/`assign_user_roles`, and `view_role`);
  // SD-85-01 seeds them onto the Admins + Managers groups and must be deployed
  // before these gates are active (see fail-closed note above — same
  // coordinated-release posture as the planner codes).
  UserView = "users.view_user",
  UserAdd = "users.add_user",
  UserChange = "users.change_user",
  UserResetPassword = "users.reset_user_password",
  UserAssignRoles = "users.assign_user_roles",
  RoleView = "users.view_role",
  // Admins-only escape hatch (custom — SD-85). Gates the "issue a temporary
  // password instead of emailing the user" checkbox on both the create and the
  // re-issue-login flows. Unlike the other `users.*` codenames (seeded to BOTH
  // Admins + Managers), the backend seeds this to the Admins group only and
  // enforces it server-side (a 403 if a temp password is requested without it),
  // so this is the one users.* gate that distinguishes an Admin from a Manager.
  UserIssueTempPassword = "users.issue_temp_password",
}

/**
 * Admin-only page gate for the Administration section (/users, /roles).
 *
 * `/auth/user/` exposes only a permission map — no role list or `is_admin`
 * flag — so we gate these pages on the one `users.*` permission the backend
 * seeds to the Admins group ONLY: `issue_temp_password`. A Manager holds
 * `view_user`/`view_role` but NOT this, so it cleanly distinguishes Admin from
 * Manager (see `UserIssueTempPassword` above).
 *
 * ⚠️ SCOPE: this hides the pages + nav entries from non-Admins in the portal.
 * It is NOT a substitute for server-side authorization — the backend
 * `/v1/users/` endpoints still authorize by their own perms, so restricting
 * those endpoints to Admins is a required backend follow-up for full
 * enforcement.
 */
export const ADMIN_PAGE_PERMISSION = PortalPermission.UserIssueTempPassword;

/**
 * Page-access permission per route (nav entry visibility + route guard).
 * A route absent from this map (e.g. the "/" landing, /version) is open to
 * everybody. `view_*` only — in-page action gating uses the enum directly.
 */
export const PAGE_VIEW_PERMISSION: Readonly<Record<string, PortalPermission>> =
  {
    "/planner": PortalPermission.PlannerView,
    "/planner/tags": PortalPermission.PlannerManageTags,
    "/maintenance": PortalPermission.MaintenanceView,
    "/reports/bookings-date-range": PortalPermission.ReportView,
    // Administration pages are Admin-only (see ADMIN_PAGE_PERMISSION).
    "/users": ADMIN_PAGE_PERMISSION,
    "/roles": ADMIN_PAGE_PERMISSION,
  };

/** Enum-key → codename pairs, for the dev override panel's checkbox list. */
export const PORTAL_PERMISSION_ENTRIES = Object.entries(PortalPermission) as [
  string,
  PortalPermission,
][];

/**
 * Dev/QA permission override panel toggle (build-time `VITE_PERMISSIONS_PANEL`).
 * Set `true` in local + staging `.env`, `false` (or unset) in production — when
 * false the panel never mounts and overrides are never read, so `can()` uses the
 * real backend permissions only.
 */
export const PERMISSION_PANEL_ENABLED =
  import.meta.env.VITE_PERMISSIONS_PANEL === "true";
